import { QRCodeSVG } from "qrcode.react";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { CheckoutService, StorefrontService } from "src/app/services/StorefrontService";
import { FanAuthService, FanSession } from "./FanSession";
import { Basket, money } from "./Basket";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";
import { ShopNav } from "./ShopNav";

/**
 * A supporter's tickets.
 *
 * Two sources, in order. Signed in, the server answers and the tickets follow
 * the person — bought on a laptop, shown on a phone at the turnstile. Signed
 * out, it falls back to the order keys written to localStorage at checkout,
 * so a guest who closed the tab can still find what they paid for.
 *
 * The fallback's limit is real and the page says so rather than hiding it:
 * one browser, one device. That is exactly the case an account fixes, which
 * is why the prompt to make one sits next to the tickets it would rescue.
 */
export function ShopTickets() {
	const { slug = "" } = useParams();
	const [club, setClub] = React.useState<any>(null);
	const [orders, setOrders] = React.useState<any[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const [seasonTickets, setSeasonTickets] = React.useState<any[]>([]);
	const [fixtures, setFixtures] = React.useState<any[]>([]);
	const [lending, setLending] = React.useState<any>(null);
	const [lendTo, setLendTo] = React.useState("");

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.get(slug);
				if (live && res.ok) setClub((await res.json()).club);
			} catch { /* header falls back to a plain title */ }
		})();
		return () => { live = false; };
	}, [slug]);

	React.useEffect(() => {
		let live = true;
		(async () => {
			// Signed in? Ask the server. An account is the whole point: the
			// tickets follow the person rather than the browser they happened
			// to buy on.
			if (FanSession.signedIn()) {
				try {
					const res = await FanAuthService.myOrders(slug);
					if (res.ok) {
						const body = await res.json();
						const fromServer = (body?.orders || []).filter((o: any) => o.tickets?.length);
						if (live) { setOrders(fromServer); setLoading(false); }
						return;
					}
				} catch { /* fall through to what this device knows */ }
			}

			// Order keys are stored as pb-order-{id} at checkout. Reading them
			// back is the whole mechanism.
			const found: { orderId: number; key: string }[] = [];
			try {
				for (let i = 0; i < window.localStorage.length; i++) {
					const name = window.localStorage.key(i);
					const match = name?.match(/^pb-order-(\d+)$/);
					if (!match) continue;
					const key = window.localStorage.getItem(name!);
					if (key) found.push({ key, orderId: Number(match[1]) });
				}
			} catch { /* private browsing — nothing to show */ }

			found.sort((a, b) => b.orderId - a.orderId);

			const loaded: any[] = [];
			for (const entry of found) {
				try {
					const res = await CheckoutService.getTickets(entry.orderId, entry.key);
					if (!res.ok) continue;
					const body = await res.json();
					// Another club's order lives under its own slug's tickets;
					// this page only shows what belongs here.
					if (body?.tickets?.length) loaded.push({ ...body, orderId: entry.orderId });
				} catch { /* skip the ones we can't reach */ }
			}

			if (live) { setOrders(loaded); setLoading(false); }
		})();
		return () => { live = false; };
	}, [slug]);

	// A season ticket holder can lend a seat they can't use. Only fetched when
	// signed in — a guest has no season ticket to lend.
	React.useEffect(() => {
		let live = true;
		if (!FanSession.signedIn()) return;
		(async () => {
			try {
				const [mine, schedule] = await Promise.all([
					FanAuthService.mySeasonTickets(),
					StorefrontService.getSchedule(slug, false)
				]);
				if (!live) return;
				if (mine.ok) setSeasonTickets((await mine.json())?.seasonTickets || []);
				if (schedule.ok) setFixtures((await schedule.json())?.fixtures || []);
			} catch { /* the section just doesn't appear */ }
		})();
		return () => { live = false; };
	}, [slug]);

	const reloadSeasonTickets = async () => {
		try {
			const res = await FanAuthService.mySeasonTickets();
			if (res.ok) setSeasonTickets((await res.json())?.seasonTickets || []);
		} catch { /* leave what's on screen */ }
	};

	const doLend = async (fixtureId: number) => {
		if (!lending) return;
		try {
			const res = await FanAuthService.lend(lending.seasonTicketId, fixtureId, {
				transfereeName: lendTo.trim() || null
			});
			if (!res.ok) {
				setError((await res.text()).replace(/^"|"$/g, "") || "That didn't work.");
				return;
			}
			setLending(null);
			setLendTo("");
			setError(null);
			reloadSeasonTickets();
		} catch {
			setError("That didn't work. Please try again.");
		}
	};

	const withdraw = async (transferId: number) => {
		try {
			await FanAuthService.withdrawLend(transferId);
			reloadSeasonTickets();
		} catch { /* nothing changes on screen */ }
	};

	const basketCount = Basket.count(slug);

	return (
		<div className="shop has-nav" style={clubTheme(club)}>
			<ShopHeader club={club} slug={slug} />

			<div className="shop-inner">
				<h2 className="shop-section-title">Your tickets</h2>

				{basketCount > 0 &&
					<Link className="shop-card" to={`/club/${slug}/basket`}>
						<div className="shop-card-body">
							<p className="shop-card-title">Basket &middot; {basketCount} {basketCount === 1 ? "ticket" : "tickets"}</p>
							<div className="shop-card-meta">Not paid for yet &mdash; tap to finish</div>
						</div>
					</Link>}

				{loading && <div className="shop-result"><span className="shop-spinner" /></div>}

				{!loading && orders.length === 0 &&
					<div className="shop-result">
						<h2>Nothing here yet</h2>
						<p>Tickets you buy on this device show up here.</p>
						<Link className="shop-btn" to={`/club/${slug}`}>See what&rsquo;s on</Link>
					</div>}

				{!loading && orders.map((order) => (
					<div key={order.orderId}>
						<h2 className="shop-section-title">
							Order #{order.orderId}
							{order.totalPence ? ` · ${money(order.totalPence)}` : ""}
						</h2>
						<div className="shop-tickets">
							{order.tickets.map((t: any, index: number) => (
								<div className={`shop-ticketcard${t.status === "Redeemed" ? " used" : ""}`} key={t.token}>
									<QRCodeSVG bgColor="#ffffff" fgColor="#111827" includeMargin size={168} value={t.token} />
									<strong>{t.ticketType || "Admission"}</strong>
									<span>
										{t.status === "Redeemed"
											? "Already used"
											: `Ticket ${index + 1} of ${order.tickets.length}`}
									</span>
								</div>
							))}
						</div>
					</div>
				))}

				{seasonTickets.length > 0 && <>
					<h2 className="shop-section-title">Season tickets</h2>
					{seasonTickets.map((st: any) => (
						<div className="shop-panel" key={st.seasonTicketId}>
							<div className="shop-line-title">{st.holderName || "Season ticket"}</div>
							<div className="shop-card-meta" style={{ marginBottom: 12 }}>
								Valid to {new Date(st.validTo).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
							</div>

							{st.lentFor?.length > 0 && st.lentFor.map((l: any) => {
								const f = fixtures.find((x: any) => x.id === l.fixtureId);
								return (
									<div className="shop-lent" key={l.transferId}>
										<span>
											Lent{l.to ? ` to ${l.to}` : ""}
											{f ? ` for ${f.isHome ? "v" : "away to"} ${f.opponent}` : ""}
										</span>
										<button onClick={() => withdraw(l.transferId)} type="button">Take it back</button>
									</div>
								);
							})}

							{lending?.seasonTicketId === st.seasonTicketId
								? <div className="shop-lendbox">
									<label htmlFor="lend-to">Who&rsquo;s having it?</label>
									<input id="lend-to" onChange={(e) => setLendTo(e.target.value)} placeholder="Their name" value={lendTo} />
									<p className="shop-card-meta">Pick the game you can&rsquo;t make:</p>
									{fixtures.filter((f: any) => f.isHome)
										.filter((f: any) => !st.lentFor?.some((l: any) => l.fixtureId === f.id))
										.slice(0, 8)
										.map((f: any) => (
											<button className="shop-btn secondary" key={f.id} onClick={() => doLend(f.id)} style={{ marginBottom: 6 }} type="button">
												v {f.opponent} &middot; {new Date(f.kickOff).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
											</button>
										))}
									<button className="shop-btn secondary" onClick={() => { setLending(null); setError(null); }} type="button">Cancel</button>
								</div>
								: <button className="shop-btn secondary" onClick={() => setLending(st)} type="button">
									Lend your seat for a game
								</button>}
						</div>
					))}
					{error && <div className="shop-error">{error}</div>}
					<p className="shop-note">
						Lending gives your seat to somebody else for one game. Your own ticket stops working
						for that game and comes back afterwards.
					</p>
				</>}

				{!loading && orders.length > 0 && !FanSession.signedIn() &&
					<p className="shop-note">
						These are the tickets bought on this device.{" "}
						<Link to={`/club/${slug}/account?new=1`}>Create an account</Link> and they&rsquo;ll follow you
						to any phone.
					</p>}

				{!loading && !FanSession.signedIn() && orders.length === 0 &&
					<p className="shop-note">
						Bought on another device?{" "}
						<Link to={`/club/${slug}/account`}>Sign in</Link> to find them.
					</p>}
			</div>

			<ShopNav slug={slug} />
		</div>
	);
}
